import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { FormSectionTitle } from 'src/Libs/flow/components'
import { Box, Grid, Typography, Stack } from '@mui/material'
import React, { useEffect } from 'react'
import { useWatch } from 'react-hook-form'
import { CarAtHomeSection } from '..'
import { ModbusTcpIdentifyButton } from './ModbusTcpIdentifyButton'
import { useInstallationForm } from 'src/Libs/flow/installation/hooks'
import { HideDirtyStateProvider } from '../../../providers'
import { useFeatureFlagsContext } from 'src/Libs/feature-flags/FeatureFlagsContext'
import { getAccessManagementOptions, getMidMeterOptions, getPhaseManagementOptions, phaseMappingOptions } from '../../utils'
import { NumberField, Select, Toggle } from 'src/Libs/inputs/react-hook-form'
import { J2Caution } from '@e3dc-react/icons'
import { SettingsBox } from 'src/Elements/Data'
import { useInstallationAccessRightsContext } from 'src/Libs/installation-rights-management/provider/InstallationAccessRightsContext'
import { cintechReference, wittyFlexReference, wittyLinkReference } from 'src/Libs/wallboxes/models/wallbox-reference'
import AccordionWrapper from '../../../components/AccordionWrapper'
import { CommonEvseDeviceSettings } from './CommonEvseDeviceSettings'
import { useEvseEditor } from '../../hooks'

/** Settings for evses with the 'modbus'-category */
export const ModbusTcp: React.FC<{ index: number }> = ({ index }) => {
  const { t } = useCustomTranslation()
  const { hasInstallationWritingRights: canEdit } = useInstallationAccessRightsContext()
  const { control, trigger } = useInstallationForm()
  const {
    featureFlags: { useEvseWittyLinkSubTypes },
  } = useFeatureFlagsContext()

  const installationId = useWatch({ control, name: 'id' })

  const [mainProtection, numberOfPhases, modbusTcp, isOcppEnabled, accessManagement] = useWatch({
    control,
    name: [
      'generalSettings.gridSettings.mainProtection',
      'generalSettings.gridSettings.numberOfPhases',
      `chargingStations.modbusTcp.${index}`,
      `chargingStations.modbusTcp.${index}.carAtHomeBehavior.isEnabled`,
      `chargingStations.modbusTcp.${index}.accessManagementOptions`,
    ],
  })

  const {
    isNew,
    brand,
    reference,
    deviceReference,
    pairingId,
    chargingMode,
    protection,
    deviceId,
    minCharge,
    fallbackMaxCurrent,
    solarRatio,
    navigationId,
    accessManagementOptions,
    initialAccessManagementOptions,
  } = modbusTcp

  const isSinglePhase = numberOfPhases === 'single-phase'

  const showAccessManagementOptionsWarning = initialAccessManagementOptions === 'freeAccess' && accessManagementOptions !== 'freeAccess'

  const isCintech = reference === cintechReference
  const isWittyLink = reference === wittyLinkReference
  const isWittyFlex = reference === wittyFlexReference

  const showLockCableOption = !isWittyFlex && (isWittyLink ? !deviceReference?.startsWith('XVL122C') : true)
  const showCpoSettings = isWittyLink && useEvseWittyLinkSubTypes
  const accessManagementDescription = getAccessManagementOptions(t, isWittyLink, isOcppEnabled).find(
    value => value.value === accessManagement
  )?.description

  const { isDirty, isError, isExpanded, toggleExpanded, canPair, handlePairing, displayName, handleDelete } = useEvseEditor({
    evseType: 'modbusTcp',
    device: modbusTcp,
    deviceCategoryName: 'ModbusTCP Evse',
    index,
  })

  // Revalidate when one of these values change
  useEffect(() => {
    trigger(`chargingStations.modbusTcp.${index}`)
  }, [index, trigger, mainProtection, protection, minCharge, fallbackMaxCurrent, solarRatio, chargingMode])

  return (
    <HideDirtyStateProvider hideDirtyState={isNew}>
      <Box component={'section'} id={navigationId}>
        <AccordionWrapper
          title={
            <FormSectionTitle
              name={displayName}
              onClick={handleDelete}
              isDirty={isDirty}
              isError={isError}
              rightContent={
                installationId !== null && (
                  <ModbusTcpIdentifyButton installationId={installationId} modbusTcpIndex={index} isWittyLink={isWittyLink} />
                )
              }
            />
          }
          expanded={isExpanded}
          callback={toggleExpanded}
        >
          <Grid container rowSpacing={2} sx={{ mb: 4 }}>
            <CommonEvseDeviceSettings
              evseType="modbusTcp"
              index={index}
              pairingId={pairingId}
              brand={brand}
              reference={reference}
              deviceReference={deviceReference}
              deviceId={deviceId}
              canEdit={canEdit}
              canPair={canPair}
              handlePairing={handlePairing}
              afterProtection={
                <>
                  <SettingsBox
                    title={t('installation.evseEditor.fallback-max-current')}
                    suffix={
                      <NumberField
                        label={t('installation.evseEditor.fallback-max-current')}
                        suffix={t('affixes.ampere')}
                        name={`chargingStations.modbusTcp.${index}.fallbackMaxCurrent`}
                        min={0}
                        max={64}
                        disabled={!canEdit}
                      />
                    }
                  />
                  {!isWittyFlex && (
                    <SettingsBox
                      title={t('installation.storageEditor.phaseManagementOptions')}
                      suffix={
                        <Select
                          name={`chargingStations.modbusTcp.${index}.phaseManagement`}
                          label={t('installation.storageEditor.phaseManagementOptions')}
                          options={getPhaseManagementOptions(t)}
                          disabled={isSinglePhase || !canEdit}
                        />
                      }
                    />
                  )}
                  <SettingsBox
                    title={t('installation.storageEditor.phase-mapping')}
                    suffix={
                      <Select
                        name={`chargingStations.modbusTcp.${index}.phaseMapping`}
                        options={phaseMappingOptions}
                        label={t('installation.storageEditor.phase-mapping')}
                        disabled={isSinglePhase || !canEdit}
                      />
                    }
                  />
                </>
              }
              afterMinimumCharge={
                <>
                  {isCintech && (
                    <SettingsBox
                      title={t('installation.evseEditor.midSettingSubTitle')}
                      description={t('installation.evseEditor.midSettingNote')}
                      suffix={
                        <Select
                          label={t('installation.evseEditor.mid-meter-options')}
                          name={`chargingStations.modbusTcp.${index}.midMeter`}
                          options={getMidMeterOptions(t)}
                          disabled={!canEdit}
                        />
                      }
                    />
                  )}
                  {showCpoSettings && <CarAtHomeSection index={index} />}
                  {showLockCableOption && (
                    <SettingsBox
                      title={t('installation.evseEditor.lockCable.title')}
                      description={t('installation.evseEditor.lockCable.description')}
                      suffix={
                        <Stack sx={{ flexDirection: 'row', justifyContent: 'start', alignItems: 'center' }}>
                          <Toggle disabled={isNew || !canEdit} name={`chargingStations.modbusTcp.${index}.isCableLocked`} />
                          <Typography variant="body1" display="flex">
                            {t('installation.evseEditor.lockCable.label')}
                          </Typography>
                        </Stack>
                      }
                    />
                  )}
                  <SettingsBox
                    title={t('installation.evseEditor.led-intensity')}
                    suffix={
                      <NumberField
                        label={t('installation.evseEditor.led-intensity')}
                        suffix={t('affixes.percent')}
                        name={`chargingStations.modbusTcp.${index}.ledIntensityPercentage`}
                        min={30}
                        max={100}
                        disabled={!canEdit}
                      />
                    }
                  />
                  <SettingsBox
                    title={t('installation.storageEditor.accessManagementOptions')}
                    description={
                      <>
                        <Typography variant="body2" mt={2}>
                          {accessManagementDescription}
                        </Typography>
                        {showAccessManagementOptionsWarning && (
                          <Stack sx={{ flexDirection: 'row', mt: 2, gap: 1 }}>
                            <J2Caution color="primary" />
                            <Typography variant="body2">{t('installation.storageEditor.accessManagementWarning')}</Typography>
                          </Stack>
                        )}
                      </>
                    }
                    suffix={
                      <Select
                        name={`chargingStations.modbusTcp.${index}.accessManagementOptions`}
                        options={getAccessManagementOptions(t, isWittyLink, isOcppEnabled)}
                        label={t('installation.storageEditor.accessManagementOptions')}
                        disabled={!canEdit}
                      />
                    }
                  />
                </>
              }
            />
          </Grid>
        </AccordionWrapper>
      </Box>
    </HideDirtyStateProvider>
  )
}
